import { useState, useEffect, useRef } from 'react';
import { Workbox } from 'workbox-window';

const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 60 minutes

interface UseServiceWorkerReturn {
  needsRefresh: boolean;
  updateSW: () => void;
  isRegistered: boolean;
}

export function useServiceWorker(): UseServiceWorkerReturn {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const wbRef = useRef<Workbox | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const wb = new Workbox('/sw.js');
    wbRef.current = wb;

    wb.addEventListener('waiting', () => {
      setNeedsRefresh(true);
    });

    wb.addEventListener('controlling', () => {
      window.location.reload();
    });

    const startInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (document.visibilityState === 'visible') {
        intervalRef.current = setInterval(() => {
          wb.update();
        }, SW_UPDATE_INTERVAL_MS);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        wb.update();
        startInterval();
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    void wb.register().then(() => {
      setIsRegistered(true);
      startInterval();
    });

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const updateSW = () => {
    if (wbRef.current) {
      wbRef.current.messageSkipWaiting();
    }
  };

  return { needsRefresh, updateSW, isRegistered };
}
